<template>
  <div class="calendar">
    <div class="nav">
      <font-awesome-icon v-if="hasPrev" class="prev" :icon="faChevronLeft" @click="prev" />
      <div class="month mid">
        <strong>{{ $t(month) }}</strong>
        <span>, </span>
      </div>
      <select-input
        :value="year"
        :items="availableYears"
        borderless
        padding-less
        @input="setYear"
      />
      <font-awesome-icon v-if="hasNext" class="next" :icon="faChevronRight" @click="next" />
    </div>

    <div class="grid">
      <table class="calendar">
        <thead>
          <tr>
            <th>{{ $t('MO') }}</th>
            <th>{{ $t('TU') }}</th>
            <th>{{ $t('WE') }}</th>
            <th>{{ $t('TH') }}</th>
            <th>{{ $t('FR') }}</th>
            <th>{{ $t('SA') }}</th>
            <th>{{ $t('SU') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, i) in Array(6)"
            :key="`calendar-row-${i}`"
          >
            <td
              v-for="cell in dates.slice(i * 7, i * 7 + 7)"
              :key="cell.key"
              :class="{
                highlight: cell.highlight,
                start: cell.start,
                end: cell.end,
                active: cell.active
              }"

              @click="() => select(cell.key)"
            >
              {{ cell.date }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import SelectInput from '@/components/SelectInput';
import { MIN_DATE, DATE_FORMAT } from '@/consts';

const maxDate = moment().add(1, 'year').endOf('month');

export default {
  name: 'Calendar',
  components: {
    FontAwesomeIcon,
    SelectInput
  },
  props: {
    date: {
      type: String,
      default: moment().toISOString()
    },
    range: {
      type: Array,
      default: () => []
    },
    displayMonth: {
      type: String,
      default: ''
    },
    yearRange: {
      type: Array,
      default: () => [moment(MIN_DATE).year(), moment().year() + 1]
    }
  },
  setup(props, { emit }) {
    const availableYears = computed(() => Array(props.yearRange[1] - props.yearRange[0] + 1).fill(0).map((item, i) => ({
      value: props.yearRange[0] + i,
      text: props.yearRange[0] + i
    })));

    const minDate = computed(() => (moment([props.yearRange[0]]).format(DATE_FORMAT)));

    const from = computed(() => moment(props.range?.length ? props.range[0] : props.date).startOf('day'));
    const to = computed(() => moment(props.range?.length ? props.range[1] : props.date).startOf('day'));

    const displayMonthWrapper = computed(() => (props.displayMonth || to.value.toISOString()));
    const displayMonth = ref(moment(displayMonthWrapper.value).endOf('month').startOf('day'));

    const month = computed(() => displayMonth.value.format('MMMM').toUpperCase());
    const year = computed(() => displayMonth.value.year());

    const hasNext = computed(() => (maxDate.diff(displayMonth.value, 'months') > 1));

    const hasPrev = computed(() => (displayMonth.value.diff(minDate.value, 'months') >= 1));

    const prev = () => {
      if (hasPrev.value) {
        displayMonth.value = displayMonth.value.clone().subtract(1, 'months');
      }
    };

    const next = () => {
      if (hasNext.value) {
        displayMonth.value = displayMonth.value.clone().add(1, 'months');
      }
    };

    const setYear = (val) => {
      displayMonth.value = displayMonth.value.clone().year(val);
    };

    const dates = computed(() => {
      const d = displayMonth.value.clone().startOf('month');
      d.subtract(d.day(), 'days');

      if (d.date() === 1) {
        d.subtract(7, 'days');
      }

      d.add(1, 'days');

      return Array(6 * 7).fill(0).map(() => {
        let dd = '';

        if (d.month() === displayMonth.value.month()) {
          dd = d.date();
        }

        const cell = {
          date: dd,
          key: d.format(DATE_FORMAT),
          highlight: (!!dd && (d.diff(from.value, 'days') >= 0) && (to.value.diff(d, 'days') >= 0)),
          start: (!!dd && (d.diff(from.value, 'days') >= 0) && (to.value.diff(d, 'days') >= 0) && ((d.diff(from.value, 'days') === 0) || (d.day() === 1))),
          end: (!!dd && (d.diff(from.value, 'days') >= 0) && (to.value.diff(d, 'days') >= 0) && ((to.value.diff(d, 'days') === 0) || (d.day() === 0))),
          active: (!!dd && ((d.diff(from.value, 'days') === 0) || (d.diff(to.value, 'days') === 0)))
        };

        d.add(1, 'day');

        return cell;
      });
    });

    const select = (val) => {
      emit('dateChange', val);
    };

    watch(displayMonth, (val) => {
      emit('displayMonthChange', val.format(DATE_FORMAT));
    });

    watch(displayMonthWrapper, (wr) => {
      if (displayMonth.value.format(DATE_FORMAT) !== wr) {
        displayMonth.value = moment(wr).endOf('month').startOf('day');
      }
    });

    return {
      availableYears,
      faChevronLeft,
      faChevronRight,
      month,
      year,
      hasNext,
      hasPrev,
      prev,
      next,
      setYear,
      dates,
      select
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

.calendar {
  padding: 10px;
  width: 250px;
  font-size: 15px;
  display: inline-block;

  .nav {
    position: relative;
    padding: 3px 0;
    text-align: center;
    display: block;
    user-select: none;
    padding-left: 25px;

    .prev, .next {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
    }

    .prev {
      left: 0;
      right: auto;
    }

    .next {
      left: auto;
      right: 0;
    }

    .select-input {
      z-index: 1;
      width: 60px;

      .display {
        height: 24px;
      }
    }

    .mid {
      display: inline-block;
      line-height: 0;
      padding-right: 3px;
    }
  }

  .grid {
    table.calendar {
      font-size: 14px;
      user-select: none;

      thead {
        border-bottom: 1px solid $alto;
      }

      tbody {
        &::before {
          content:"@";
          display:block;
          line-height:10px;
          text-indent:-99999px;
        }

        tr {
          border-bottom: 5px solid $white;
        }
      }

      th, td {
        width: 35px;
        height: 15px;
        text-align: center;
        position: relative;

        &::before, &::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: $columbiaBlue;
          z-index: -2;
          opacity: 0;
        }

        &::after {
          z-index: -1;
          border-radius: 20px;
          transition: all .2s ease-in-out;
        }
      }

      td {
        cursor: pointer;
        transition: all .2s ease-in-out;

        &.highlight {
          &::before {
            opacity: 1;
          }
        }

        &.start {
          &::before {
            opacity: 1;
            border-top-left-radius: 20px;
            border-bottom-left-radius: 20px;
          }
        }

        &.end {
          &::before {
            opacity: 1;
            border-top-right-radius: 20px;
            border-bottom-right-radius: 20px;
          }
        }

        &.active {
          color: $white;

          &::after {
            background-color: $blueNcs;
            opacity: 1;
          }
        }

        &:hover {
          color: $white;

          &::after {
            background-color: $geranium;
            opacity: 1;
          }
        }
      }
    }
  }
}
</style>
